import { FLAGS } from '@iheartjane/feature-flags';
import {
  CompareProducts,
  getDisabledWizardCategories,
  shouldShowGold as showGold,
} from '@iheartjane/menu';
import { useEffect } from 'react';
import type { LoaderFunctionArgs } from 'react-router';
import {
  Outlet,
  useLoaderData,
  useMatches,
  useSearchParams,
} from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { StoreBanner } from '@jane/bloom/components/communicationBanner/storeBanner.tsx';
import { Header } from '@jane/bloom/components/header';
import { MobileLoginCallout } from '@jane/bloom/components/header/prompt/mobileLoginCallout';
import { BloomWrapper } from '@jane/bloom/components/wrapper';
import { fetchDmFacets } from '@jane/bloom/dataAccess/dm/facets.server';
import { fetchLoyaltyPoints } from '@jane/bloom/dataAccess/marketplace/loyaltyPoints.server';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useOptimisticBagSizeGetter } from '@jane/bloom/hooks/useBagSize';
import { useHasStoreBanner } from '@jane/bloom/hooks/useHasStoreBanner';
import { usePrompt } from '@jane/bloom/hooks/usePrompt';
import { useRefreshCart } from '@jane/bloom/hooks/useRefreshCart';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import { JANE_GOLD_CONFIG_CONTEXT } from '@jane/bloom/middlewares/janeGoldConfigContext.server';
import { MENU_CONTEXT } from '@jane/bloom/middlewares/menuContext.server';
import {
  trackClickedCompare,
  trackClosedCompareModal,
  trackOpenedCompareModal,
} from '@jane/bloom/services/tracking/track';
import { isPromptVisible } from '@jane/bloom/sessions/prompt.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import { initializeDMContext } from '@jane/bloom/utils/dm/dmContext.server';
import { durations } from '@jane/bloom/utils/durations';
import { BloomError } from '@jane/bloom/utils/errors/bloomError';
import { PROMPTS } from '@jane/bloom/utils/prompts';
import { SIGN_IN_PROMPT_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';
import {
  accountPages,
  isRouteInGroup,
  pdpPages,
  plpPages,
} from '@jane/bloom/utils/routes/routeIds';
import { getCustomLanguage } from '@jane/bloom/viewModels/getCustomLanguage.server';
import { buildNavCategories } from '@jane/bloom/viewModels/navigation/buildNavCategories';

import styles from './styles.module.css';
import type { LoaderData } from './types';

export async function loader({ context, request }: LoaderFunctionArgs) {
  const { menu } = context.get(MENU_CONTEXT);
  const { pinnedStore, shoppingPreferences } = await globalSubloader(
    request,
    menu
  );
  const customLanguage = getCustomLanguage(pinnedStore);

  const { sdk } = await initializeDMContext({ context, request });

  const storewideFacets = await fetchDmFacets(sdk, {
    storeId: shoppingPreferences.storeId,
  });

  const hasAiqIntegration = pinnedStore.crm_integration === 'alpineiq';

  if (!storewideFacets) {
    throw new BloomError({ message: 'Store facets were not fetched' });
  }

  const userIdentity = await getUserIdentity(request, menu.cookie_prefix);

  const loyaltyPoints = hasAiqIntegration
    ? await fetchLoyaltyPoints({
        jwtToken: userIdentity?.jwtToken,
        storeId: pinnedStore.id,
      })
    : null;

  const { goldConfig } = context.get(JANE_GOLD_CONFIG_CONTEXT);
  const shouldShowGold = showGold({
    appMode: 'bloom',
    goldConfig,
    state: pinnedStore.state,
    storeId: pinnedStore.id,
  });

  const facetsWithGold = {
    ...storewideFacets['kind'],
    ...(storewideFacets['has_brand_discount']
      ? {
          brand_specials: storewideFacets['has_brand_discount']['true'],
        }
      : {}),
  };

  let displayCategories = buildNavCategories({
    kindFacet: shouldShowGold ? facetsWithGold : storewideFacets['kind'],
    menuTabs: pinnedStore.menu_tabs,
    productTypeLabels: customLanguage.productTypeLabels,
  });

  if (!shouldShowGold) {
    displayCategories = displayCategories.filter(
      (category) => category.label !== 'Jane Gold'
    );
  }

  const disabledWizardCategories = getDisabledWizardCategories({
    kindFacet: storewideFacets['kind'],
    kindRootSubtypeFacet: storewideFacets['kind_root_subtype'],
  });

  const isLoggedIn = userIdentity?.user && 'authenticated' in userIdentity.user;
  const loginPromptVisible = isPromptVisible(
    request,
    PROMPTS.login,
    menu.cookie_prefix
  );
  const bankLinkingPromptVisible = isPromptVisible(
    request,
    PROMPTS.bankLinking,
    menu.cookie_prefix
  );
  const loaderData: LoaderData = {
    disabledWizardCategories,
    displayCategories,
    loyaltyPoints,
    shouldShowBankLinkingPrompt: shouldShowGold && bankLinkingPromptVisible,
    shouldShowLoginPrompt: !isLoggedIn && loginPromptVisible,
  };

  return loaderData;
}

// Wraps the shared compare modal so the add-to-cart fetcher subscription
// (via useOptimisticBagSizeGetter) is scoped here rather than re-rendering the
// whole menu layout on every fetcher change. Gives each add button its live
// "Add (N)" in-bag count.
function CompareModal() {
  const getCountInCart = useOptimisticBagSizeGetter();
  return (
    <CompareProducts.Modal
      addToCartLabel="Add"
      getCountInCart={getCountInCart}
      isMobile={false}
    />
  );
}

export default function MenuLayout() {
  const { loyaltyPoints, shouldShowBankLinkingPrompt, shouldShowLoginPrompt } =
    useLoaderData<LoaderData>();
  const { cookiePrefix, featureFlags } = useRootLoaderData();
  const { pinnedStore } = useStoreSwitcher();
  const compareEnabled = featureFlags[FLAGS.c1CompareProducts];
  useRefreshCart();

  const [isPromptVisible, dismissLoginPrompt] = usePrompt({
    cookiePrefix,
    dismissible: true,
    prompt: PROMPTS.login,
  });
  const [bankLinkingPromptVisible, dismissBankLinkingPrompt] = usePrompt({
    cookiePrefix,
    dismissible: true,
    expiresAfter: durations.ONE_WEEK * 2,
    prompt: PROMPTS.bankLinking,
  });
  const [searchParams] = useSearchParams();
  const signInPromptPreview =
    searchParams.get(SIGN_IN_PROMPT_QUERY_KEY) === 'true';

  const pageMatches = useMatches();
  const accountPage = Boolean(
    pageMatches.find(({ id }) => isRouteInGroup(accountPages, id))
  );
  const isPlpPage = Boolean(
    pageMatches.find(({ id }) => isRouteInGroup(plpPages, id))
  );
  const isPdpPage = Boolean(
    pageMatches.find(({ id }) => isRouteInGroup(pdpPages, id))
  );
  const shouldShowClosedStoreBanner =
    !pinnedStore?.openNow && (isPdpPage || isPlpPage);
  // signInPromptPreview bypasses both the server-side gate
  // (shouldShowLoginPrompt, which itself checks !isLoggedIn + prompt cookie)
  // and the client-side cookie check, so Business Admin can preview their
  // pending theme_draft regardless of login state or dismissed-prompt state.
  const showLoginPrompt =
    (isPdpPage || isPlpPage) &&
    (signInPromptPreview || (shouldShowLoginPrompt && isPromptVisible));
  // Whether a header sign-in should chain into the bank-linking prompt. The
  // member check (already-linked users) happens client-side in the wrapper.
  const canPromptBankLinking =
    shouldShowBankLinkingPrompt && bankLinkingPromptVisible;
  const shouldShowLoyaltyBanner =
    loyaltyPoints && loyaltyPoints.loyalty_points > 0;

  const { setHasStoreBanner } = useHasStoreBanner();
  useEffect(() => {
    setHasStoreBanner(
      Boolean(shouldShowClosedStoreBanner) || Boolean(shouldShowLoyaltyBanner)
    );
  }, [shouldShowClosedStoreBanner, shouldShowLoyaltyBanner, setHasStoreBanner]);

  const showBanner =
    Boolean(shouldShowClosedStoreBanner) || Boolean(shouldShowLoyaltyBanner);

  return (
    <BloomWrapper>
      <Header
        borderless={accountPage || showBanner}
        canPromptBankLinking={canPromptBankLinking}
        dismissBankLinkingPrompt={dismissBankLinkingPrompt}
        dismissLoginPrompt={showLoginPrompt ? dismissLoginPrompt : undefined}
        showLoginPrompt={showLoginPrompt}
      />
      {showBanner && (
        <StoreBanner
          pinnedStore={pinnedStore}
          isStoreClosed={Boolean(shouldShowClosedStoreBanner)}
          loyaltyPoints={loyaltyPoints?.loyalty_points}
        />
      )}
      <CompareProducts
        enabled={compareEnabled}
        onActiveChange={(isActive) => {
          if (isActive) trackClickedCompare();
        }}
        onOpenModal={(productCount, products) =>
          trackOpenedCompareModal(productCount, products)
        }
        onCloseModal={(productCount) =>
          trackClosedCompareModal(productCount, 'dismissed')
        }
        onClearAll={(productCount) =>
          trackClosedCompareModal(productCount, 'cleared_all')
        }
      >
        <div className={styles['layout']}>
          {showLoginPrompt && (
            <MobileLoginCallout
              canPromptBankLinking={canPromptBankLinking}
              dismissBankLinkingPrompt={dismissBankLinkingPrompt}
              onDismiss={dismissLoginPrompt}
            />
          )}
          <Outlet />
        </div>
        {compareEnabled && (
          <ClientOnly>
            {() => (
              <>
                <CompareProducts.Bar />
                <CompareModal />
              </>
            )}
          </ClientOnly>
        )}
      </CompareProducts>
    </BloomWrapper>
  );
}
